import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  buildPrequalReadiness,
  readinessTagColor,
  type PrequalReadinessState,
} from '@/components/epc/commercial-ux/prequalReadiness';
import {
  computePrequalCompleteness,
  parsePrequalRequirements,
} from '@/components/epc/commercial-ux/prequalRequirementMatrix';
import { pursuitPath } from '@/components/epc/commercial-ux/resolvePursuitOpportunityId';
import { epcInvitationKind, epcInvitationKindLabel } from '@/constants/epcInvitationKind';
import { epcCommercialService } from '@/services/epcCommercial.service';
import { formatDate } from '@/utils/format';

const { Title, Text } = Typography;

type FilterTab = 'all' | 'READY' | 'BLOCKED' | 'IN_PROGRESS';

const EpcPrequalificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filter, setFilter] = useState<FilterTab>('all');
  const kindParam = searchParams.get('kind');
  const kindFilter: 'all' | 'EOI' | 'PQ' =
    kindParam === 'EOI' || kindParam === 'PQ' ? kindParam : 'all';
  const setKindFilter = (value: 'all' | 'EOI' | 'PQ') => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('kind');
    else next.set('kind', value);
    setSearchParams(next, { replace: true });
  };

  const listQ = useQuery({
    queryKey: ['epc-prequalifications'],
    queryFn: () => epcCommercialService.listPrequalifications(),
    staleTime: 15_000,
  });

  const rows = (listQ.data || []) as Record<string, unknown>[];

  const enriched = useMemo(
    () =>
      rows.map((r) => {
        const reqRows = parsePrequalRequirements(r.meta_json || r.metaJson);
        const completeness = computePrequalCompleteness(reqRows);
        return {
          row: r,
          readiness: buildPrequalReadiness(r),
          completionPct: reqRows.length ? completeness.completionPct : null,
        };
      }),
    [rows]
  );

  const filtered = useMemo(() => {
    const byKind = enriched.filter((e) => {
      const kind = epcInvitationKind(e.row.invitation_kind || e.row.invitationKind);
      if (kindFilter === 'EOI') return kind === 'EOI';
      if (kindFilter === 'PQ') return kind !== 'EOI';
      return true;
    });
    if (filter === 'all') return byKind;
    return byKind.filter((e) => e.readiness.overall === filter);
  }, [enriched, filter, kindFilter]);

  // Legacy ?focus= links → full cockpit page
  useEffect(() => {
    const focus = searchParams.get('focus');
    if (focus) {
      navigate(`/epc/commercial/prequalifications/${encodeURIComponent(focus)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  const counts = useMemo(() => {
    const c = { READY: 0, BLOCKED: 0, IN_PROGRESS: 0 };
    for (const e of enriched) {
      c[e.readiness.overall] += 1;
    }
    return c;
  }, [enriched]);

  const openCockpit = (id: string) => {
    navigate(`/epc/commercial/prequalifications/${encodeURIComponent(id)}`);
  };

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {kindFilter === 'EOI' ? 'Expression of Interest' : 'Prequalifications'}
          </Title>
          <Text type="secondary">
            Expression of Interest is the client invitation. A blank kind is an internal
            prequalification gate. This is not a supplier enquiry.
          </Text>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/epc/commercial/invitations/new?kind=EOI')}
        >
          New
        </Button>
      </Space>

      <Tabs
        activeKey={filter}
        onChange={(k) => setFilter(k as FilterTab)}
        items={[
          { key: 'all', label: `All (${enriched.length})` },
          { key: 'READY', label: `Ready (${counts.READY})` },
          { key: 'BLOCKED', label: `Blocked (${counts.BLOCKED})` },
          { key: 'IN_PROGRESS', label: `In progress (${counts.IN_PROGRESS})` },
        ]}
        style={{ marginBottom: 8 }}
      />

      <Space style={{ marginBottom: 8 }}>
        <Select
          value={kindFilter}
          onChange={setKindFilter}
          style={{ width: 220 }}
          options={[
            { value: 'all', label: 'All records' },
            { value: 'EOI', label: 'Expression of Interest' },
            { value: 'PQ', label: 'Internal prequalification' },
          ]}
        />
      </Space>
      <Card>
        <Table
          loading={listQ.isLoading}
          rowKey={(r) => String(r.row.id)}
          dataSource={filtered}
          pagination={{ pageSize: 20 }}
          scroll={{ x: 960 }}
          onRow={(r) => ({
            onClick: () => openCockpit(String(r.row.id)),
            style: { cursor: 'pointer' },
          })}
          columns={[
            {
              title: 'Number',
              dataIndex: ['row', 'prequal_number'],
              width: 140,
            },
            { title: 'Title', dataIndex: ['row', 'title'] },
            {
              title: 'Client',
              dataIndex: ['row', 'client_name'],
              render: (v) => v || '—',
            },
            {
              title: 'Readiness',
              key: 'readiness',
              width: 130,
              render: (_: unknown, r) => (
                <Tag color={readinessTagColor(r.readiness.overall as PrequalReadinessState)}>
                  {r.readiness.overall}
                </Tag>
              ),
            },
            {
              title: 'Completion',
              key: 'completion',
              width: 100,
              render: (_: unknown, r) =>
                r.completionPct != null ? `${r.completionPct}%` : '—',
            },
            {
              title: 'Invitation',
              key: 'invitation',
              width: 180,
              render: (_: unknown, r) =>
                epcInvitationKindLabel(r.row.invitation_kind || r.row.invitationKind),
            },
            {
              title: 'Client no.',
              dataIndex: ['row', 'client_document_no'],
              width: 140,
              render: (v) => v || '—',
            },
            {
              title: 'Closing',
              dataIndex: ['row', 'closing_at'],
              width: 120,
              render: (v) => (v ? formatDate(String(v)) : '—'),
            },
            {
              title: 'Current',
              key: 'current',
              width: 90,
              render: (_: unknown, r) =>
                r.row.is_current === 1 || r.row.is_current === true ? <Tag color="blue">Current</Tag> : '—',
            },
            {
              title: 'Tender gate',
              dataIndex: ['row', 'tender_gate'],
              width: 140,
              render: (g: string) => (
                <Tag color={g === 'BLOCK' ? 'red' : g === 'WARNING' ? 'orange' : 'default'}>
                  {g || '—'}
                </Tag>
              ),
            },
            {
              title: 'Due',
              dataIndex: ['row', 'due_date'],
              width: 120,
              render: (v) => (v ? formatDate(String(v)) : '—'),
            },
            {
              title: 'Pursuit',
              key: 'pursuit',
              width: 100,
              render: (_: unknown, r) => {
                const oppId = String(r.row.opportunity_id || r.row.opportunityId || '');
                if (!oppId) return '—';
                return (
                  <Button
                    type="link"
                    style={{ padding: 0 }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(pursuitPath(oppId));
                    }}
                  >
                    Open
                  </Button>
                );
              },
            },
          ]}
        />
      </Card>
    </div>
  );
};

export default EpcPrequalificationsPage;
